import React from "react";
import { Box } from "@mui/material";
import { DotLegalButton } from "@dotlegal/dotlegal-ui-components";
import { SystemModel } from "../../../../system/systemEdit/SystemEdit.types.ts";
import { ValidationError } from "../../../../common/validationError.ts";
import { useSystemSupplerStepHooks } from "./SystemSuppliersField.hooks.ts";
import SystemSupplier from "./systemSupplier/SystemSupplier.tsx";
import { useTranslation } from "../../../../localization/useTranslation.ts";
import useCountryHook from "../../../../common/hooks/useCountryList.ts";
import { SystemStandardField, SystemSupplierPermissions } from "../../../EntityTemplate.types.ts";

export interface SystemSuppliersFieldProps {
    system?: SystemModel;
    validationErrors: Array<ValidationError>;
    readOnly?: boolean;
    disabled: boolean;
    isPreview: boolean;
    supplierPermissions: SystemSupplierPermissions;
}

function SystemSuppliersField(props: SystemSuppliersFieldProps) {
    const { translateString } = useTranslation();

    if (!props.system || props.isPreview) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <DotLegalButton buttonType="secondary" disabled onClick={() => {}}>
                    {translateString("addSupplier")}
                </DotLegalButton>
            </Box>
        );
    }

    return <SystemSuppliersFieldContent {...props} system={props.system} />;
}

function SystemSuppliersFieldContent(props: SystemSuppliersFieldProps & { system: SystemModel }) {
    const { translateString } = useTranslation();

    const {
        legalEntityLoading,
        legalEntityData,
        transferBasisLoading,
        transferBasisData,
        getExpandedId,
        setExpandedId,
        onExpandClick,
        addSupplier,
        isAddingSupplier,
        removeSupplier,
    } = useSystemSupplerStepHooks({ system: props.system, validationErrors: props.validationErrors, readOnly: props.disabled });
    const { selectableCountries, countriesIsLoading } = useCountryHook();

    const suppliers = props.system.suppliers;
    const error = props.validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemSuppliers].toString())?.error;

    return (
        <>
            {suppliers.map((supplier) => (
                <SystemSupplier
                    system={props.system}
                    key={supplier.id}
                    supplier={supplier}
                    readOnly={props.disabled}
                    legalEntityData={legalEntityData}
                    legalEntityLoading={legalEntityLoading}
                    transferBasisData={transferBasisData}
                    transferBasisLoading={transferBasisLoading}
                    countryList={selectableCountries}
                    countriesLoading={countriesIsLoading}
                    getExpandedId={() => getExpandedId()}
                    onExpandedClick={() => onExpandClick(supplier.id)}
                    validationErrors={props.validationErrors}
                    setExpandedId={setExpandedId}
                    removeSupplier={() => removeSupplier(supplier.id)}
                    supplierPermissions={props.supplierPermissions}
                />
            ))}

            <Box sx={(theme) => ({ marginTop: theme.spacing(3), display: "flex", justifyContent: "center" })}>
                <DotLegalButton
                    isLoading={isAddingSupplier}
                    disabled={suppliers.some((x) => x.legalEntityId === undefined || x.legalEntityId === null) || props.disabled}
                    buttonType="secondary"
                    onClick={() => {
                        addSupplier();
                    }}
                >
                    {translateString("addSupplier")}
                </DotLegalButton>
            </Box>
            {error && (
                <Box
                    sx={(theme) => ({
                        fontSize: theme.typography.pxToRem(12),
                        color: theme.palette.error.main,
                        textAlign: "center",
                        marginTop: theme.spacing(2),
                    })}
                >
                    {error}
                </Box>
            )}
        </>
    );
}

export default SystemSuppliersField;
